import React, { useEffect, useState, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  parseCurrency,
  updateData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
  Size_Id: 0,
  Size_Code: "",
  Size_Name: "",
  Size_Status: "Y",
};

const SizeForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        comp_id: parseCurrency(selectedData.Comp_Id, 10),
        Size_Id: parseCurrency(selectedData.Size_Id, 10),
        Size_Code: selectedData.Size_Code || "",
        Size_Name: selectedData.Size_Name || "",
        Size_Status: selectedData.Size_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const { Size_Id, comp_id, ...dataToInsert } = formData;
      const fields = "Size_Code,Size_Name,Size_Status,Comp_Id";
      
      // ตรวจสอบว่ามีค่าที่จำเป็นหรือไม่
      if (!dataToInsert.Size_Code || !dataToInsert.Size_Name) {
        getAlert("WARNING", "โปรดกรอกรหัสไซส์และชื่อไซส์");
        return;
      }
      
      // แปลง Size_Code เป็นตัวพิมพ์ใหญ่ และส่งเป็น array ธรรมดา (ไม่ใช่ array of arrays)
      const sizeCode = String(dataToInsert.Size_Code || "").toUpperCase().trim();
      const sizeName = String(dataToInsert.Size_Name || "").trim();
      const sizeStatus = String(dataToInsert.Size_Status || "Y");
      const compId = Number(comp_id) || Number(window.localStorage.getItem("company")) || 0;
      
      // ตรวจสอบว่าค่าไม่ว่าง
      if (!sizeCode || sizeCode.length === 0) {
        getAlert("WARNING", "โปรดกรอกรหัสไซส์");
        return;
      }
      if (!sizeName || sizeName.length === 0) {
        getAlert("WARNING", "โปรดกรอกชื่อไซส์");
        return;
      }
      if (!compId || compId === 0) {
        getAlert("WARNING", "ไม่พบ Company ID");
        return;
      }
      
      const values = [
        sizeCode,
        sizeName,
        sizeStatus,
        compId,
      ];
      
      // Debug: ตรวจสอบ fields และ values
      console.log("Insert Size - Fields:", fields);
      console.log("Insert Size - Values:", values);
      console.log("Insert Size - Fields count:", fields.split(", ").length);
      console.log("Insert Size - Values count:", values.length);
      
      await insertData("Tb_Set_ItmSize", fields, values);
      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error inserting data:", error);
      getAlert(
        "FAILED",
        "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const where = { Size_Id: formData.Size_Id };
      const { Size_Id, comp_id, ...values } = formData;
      // แปลง Size_Code เป็นตัวพิมพ์ใหญ่
      const updateValues = {
        ...values,
        Size_Code: String(values.Size_Code || "").toUpperCase().trim(),
        Comp_Id: comp_id,
      };
      const res = await updateData("Tb_Set_ItmSize", updateValues, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    if (!selectedData?.Size_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (isConfirm) {
        await deleteData(
          "Tb_Set_ItmSize",
          `WHERE Size_Id = ${selectedData.Size_Id}`
        );
        await callInitialize();
      }
    });
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.Size_Code || !formData.Size_Name) {
      getAlert("WARNING", "โปรดกรอกรหัสไซส์และชื่อไซส์");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.Size_Code, formData.Size_Name, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            callInitialize();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit, callInitialize]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const handleBack = () => {
    callInitialize();
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/tbsetsize"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-ruler" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>ข้อมูลไซส์</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-4">
          <label style={labelStyle}>รหัสไซส์ (ภาษาอังกฤษ)</label>
          <input
            type="text"
            className="form-control"
            name="Size_Code"
            value={formData.Size_Code || ""}
            onChange={handleChange}
            style={inputStyle}
            placeholder="เช่น S, M, L, XL"
            onInput={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />
          <small style={{ color: "#E13F57", fontSize: "12px" }}>
            ⚠️มีผลกับการสร้างรหัสสินค้า
          </small>
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>ชื่อไซส์</label>
          <input
            type="text"
            className="form-control"
            name="Size_Name"
            value={formData.Size_Name || ""}
            onChange={handleChange}
            style={inputStyle}
            placeholder="เช่น S, M, L, XL"
          />
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="Size_Status"
            value={formData.Size_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default SizeForm;
